import ErrorMessage from "@/components/ErrorMessage";
import Layout from "@/components/elements/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForgotPassword } from "@/hooks";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";
import toast from "react-hot-toast";
import navLogo from "../assets/images/nav-logo.svg";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CircleNotch } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const {
    email,
    setEmail,
    isLoading,
    requestResetPassword,
    isSuccess,
    errors,
  } = useForgotPassword();

  const router = useRouter();

  const handleOnFormSubmit = (evt) => {
    evt.preventDefault();
    requestResetPassword();
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(`We just sent a link to ${email} to reset your password.`, {
        duration: 6000,
      });
      router.push("/");
    }
  }, [isSuccess]);

  return (
    <div className='items-center lg:flex lg:h-screen lg:space-x-20 bg-gradient-to-t from-background to-white/10'>
      <div className='hidden h-full p-4 lg:block lg:w-1/2'>
        <div className='relative w-full h-full overflow-hidden shadow-xl rounded-xl'>
          <Image
            src='https://images.pexels.com/photos/1182334/pexels-photo-1182334.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
            alt=''
            layout='fill'
            objectFit='cover'
          />
        </div>
      </div>
      <div className='h-full px-4 lg:px-0 lg:flex-1'>
        <div className='flex flex-col items-start justify-between h-full py-5 lg:py-8 lg:max-w-md'>
          <Link legacyBehavior href={"/"} passHref>
            <a className='flex-shrink-0 inline-block relative h-12 aspect-[189/48] -ml-1.5'>
              <Image src={navLogo.src} alt='Lucidity Insights' layout='fill' />
            </a>
          </Link>
          <div className='w-full pt-2 pb-5 2xl:py-0'>
            <Button
              variant='link'
              className='p-0 group'
              onClick={() => router.back()}
            >
              <span className='inline-flex items-center justify-center transition-colors rounded-full w-7 h-7 group-hover:bg-white/20 bg-white/10'>
                <ArrowLeft className='text-sm' />
              </span>
              <span className='ml-2 text-xs font-normal opacity-60'>
                Go Back
              </span>
            </Button>
            <div>
              <h1 className='mt-4 text-5xl font-bold leading-tight text-white 2xl:pt-8 lg:text-6xl font-display'>
                Forgot Password
              </h1>
              <h2 className='mt-2 text-sm lg:text-base lg:mt-4 text-white/60'>
                Enter your registered email below. We will send you the link to
                reset your current password.
              </h2>
            </div>

            <div className='py-6 lg:py-8'>
              <form
                onSubmit={handleOnFormSubmit}
                className='grid grid-cols-12 gap-4'
              >
                <div className='col-span-12 space-y-1.5 text-left'>
                  <Label className='text-xs'>Registered Email</Label>
                  <Input
                    type='email'
                    required
                    onInput={(evt) => setEmail(evt.target.value)}
                    className='bg-white/5 hover:bg-white/10'
                  />
                  {errors.message && (
                    <small className='block mt-1 text-red-500'>
                      {errors.message}
                    </small>
                  )}
                </div>
                <div className='flex flex-col items-center col-span-12 gap-4'>
                  <Button
                    type='submit'
                    className='w-full text-white bg-gradient-to-r from-yellow-1 to-red-1'
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <CircleNotch
                        weight='bold'
                        className='mr-2 text-lg animate-spin'
                      />
                    )}
                    {isLoading ? "Processing..." : "Send Reset Link"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
          <span className='block w-full text-xs text-center opacity-30 lg:text-left'>
            &copy; {new Date().getFullYear()} Lucidity Insights. All Rights
            Reserved
          </span>
        </div>
      </div>
    </div>
  );
}
